import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import MoleculeCanvas from "./MoleculeCanvas";

const VIALS = [
  {
    src: "/vials/tirzepatide.webp",
    x: "3%", y: "10%", size: 175, rot: -6, glow: "#D12A2F", delay: 0.0,
  },
  {
    src: "/vials/retatrutide.webp",
    x: "76%", y: "8%", size: 190, rot: 5, glow: "#5A2A82", delay: 0.6,
  },
  {
    src: "/vials/ghk.webp",
    x: "80%", y: "60%", size: 165, rot: -3, glow: "#007A78", delay: 1.2,
  },
  {
    src: "/vials/bac-water.webp",
    x: "1%", y: "62%", size: 180, rot: 7, glow: "#00529B", delay: 1.8,
  },
];

export default function HeroVisual({ mouseRef }) {
  const containerRef = useRef();
  const [mp, setMp] = useState({ x: 0, y: 0 });

  useEffect(() => {
    // Subtle mouse tracking — only for aurora glows, NOT vials
    const onMove = (e) => {
      const el = containerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left - r.width / 2) / (r.width / 2);
      const y = (e.clientY - r.top - r.height / 2) / (r.height / 2);
      setMp({ x, y });
      if (mouseRef) mouseRef.current = { x, y };
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [mouseRef]);

  const { scrollY } = useScroll();
  const scaleScroll = useTransform(scrollY, [0, 500], [1, 0.95]);

  return (
    <div ref={containerRef} className="relative w-full h-[55vh] md:h-[80vh] min-h-[320px] md:min-h-[560px] overflow-hidden">
      {/* Aurora glow blobs behind everything — subtle mouse follow */}
      <motion.div
        className="absolute pointer-events-none"
        style={{
          left: "20%", top: "10%",
          width: 520, height: 520,
          background: "radial-gradient(circle at center, rgba(0,82,155,0.28), rgba(0,82,155,0) 70%)",
          filter: "blur(40px)",
          x: mp.x * -18,
          y: mp.y * -12,
        }}
        animate={{ scale: [1, 1.12, 1] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute pointer-events-none"
        style={{
          right: "10%", top: "20%",
          width: 480, height: 480,
          background: "radial-gradient(circle at center, rgba(90,42,130,0.26), rgba(90,42,130,0) 70%)",
          filter: "blur(50px)",
          x: mp.x * 14,
          y: mp.y * 12,
        }}
        animate={{ scale: [1, 1.2, 1] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      />
      <motion.div
        className="absolute pointer-events-none"
        style={{
          left: "40%", bottom: "5%",
          width: 620, height: 620,
          background: "radial-gradient(circle at center, rgba(0,122,120,0.22), rgba(0,122,120,0) 70%)",
          filter: "blur(60px)",
          x: mp.x * 10,
        }}
        animate={{ scale: [1, 1.18, 1] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay: 2 }}
      />

      {/* Fine dotted technical grid */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.15]"
        style={{
          backgroundImage: "radial-gradient(circle, #111 1px, transparent 1px)",
          backgroundSize: "26px 26px",
          maskImage: "radial-gradient(ellipse at center, black 40%, transparent 78%)",
          WebkitMaskImage: "radial-gradient(ellipse at center, black 40%, transparent 78%)",
        }}
      />

      {/* Radial spotlight behind molecule */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 50% 50%, rgba(255,255,255,0.9) 0%, rgba(247,247,246,0) 55%)",
        }}
      />

      {/* Center molecule 3D */}
      <motion.div
        className="absolute inset-0 flex items-center justify-center z-0"
        style={{ scale: scaleScroll }}
      >
        <div className="relative">
          <motion.div
            className="absolute inset-0 pointer-events-none"
            style={{
              width: 640, height: 640,
              left: -60, top: -60,
              borderRadius: "50%",
              background: "radial-gradient(circle at center, rgba(17,17,17,0.08), transparent 60%)",
            }}
            animate={{ scale: [1, 1.08, 1], opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          />
          <svg width="640" height="640" viewBox="0 0 640 640" className="absolute -inset-16 pointer-events-none hidden md:block" style={{ opacity: 0.35 }}>
            <circle cx="320" cy="320" r="200" stroke="#111" strokeWidth="0.6" fill="none" strokeDasharray="1 6" />
            <circle cx="320" cy="320" r="260" stroke="#111" strokeWidth="0.6" fill="none" strokeDasharray="1 8" />
            <circle cx="320" cy="320" r="310" stroke="#111" strokeWidth="0.4" fill="none" strokeDasharray="2 12" />
          </svg>
          <div className="w-[240px] h-[240px] md:w-[420px] md:h-[420px] max-w-full relative opacity-40 md:opacity-70">
            <MoleculeCanvas mouseRef={mouseRef} />
          </div>
        </div>
      </motion.div>

      {/* Vials — clean fixed-position with gentle floating animation. z-10 to sit above molecule. */}
      {VIALS.map((v, i) => (
        <motion.div
          key={i}
          className="absolute pointer-events-none z-10"
          style={{
            left: v.x,
            top: v.y,
            width: `clamp(90px, 22vw, ${v.size}px)`,
            transform: `rotate(${v.rot}deg)`,
          }}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.4, delay: 0.4 + i * 0.15, ease: [0.16, 1, 0.3, 1] }}
        >
          <div
            className="relative"
            style={{
              animation: `floaty 6s ease-in-out ${v.delay}s infinite`,
            }}
          >
            {/* Colored glow behind vial */}
            <div
              className="absolute -inset-8 pointer-events-none"
              style={{
                background: `radial-gradient(circle at center, ${v.glow}55, transparent 65%)`,
                filter: "blur(28px)",
              }}
            />
            <img
              src={v.src}
              alt=""
              draggable={false}
              className="w-full h-auto relative"
              style={{
                filter: `drop-shadow(0 25px 40px ${v.glow}40) drop-shadow(0 8px 12px rgba(0,0,0,0.15))`,
              }}
            />
            <div
              className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-3/4 h-4 pointer-events-none"
              style={{
                background: `radial-gradient(ellipse at center, ${v.glow}30, transparent 70%)`,
                filter: "blur(6px)",
              }}
            />
          </div>
        </motion.div>
      ))}

      {/* Corner labels */}
      <motion.div
        className="absolute top-6 left-6 font-mono-label text-[10px] text-neutral-500 z-20"
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.6, duration: 0.6 }}
      >
        [ FIG.01 · MOLECULAR STRUCTURE ]
      </motion.div>
      <motion.div
        className="absolute bottom-6 right-6 font-mono-label text-[10px] text-neutral-500 z-20"
        initial={{ opacity: 0, x: 10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.7, duration: 0.6 }}
      >
        [ BATCH · PV-2026 ]
      </motion.div>

      {/* Sweeping shimmer line */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
      >
        <motion.div
          className="absolute top-1/2 left-0 right-0 h-px"
          style={{ background: "linear-gradient(90deg, transparent, rgba(17,17,17,0.3), transparent)" }}
          animate={{ y: [-200, 200, -200] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>
    </div>
  );
}
